import { useQuery } from '@apollo/client/react';

import { LIMIT_FOR_FETCHING_ITEMS } from '~/shared/constants';
import postFragment from '~/shared/graphql-queries/posts/post-fragment';
import { itemsPagination, readQuery } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType, PaginationVariablesType } from '~/shared/types/general-type';
import type { PostType } from '~/shared/types/post-type';

export type GetFilterType =
  | {
      categoryAlias: string | string[] | null | undefined;
    }
  | {
      tagAlias: string | string[] | null | undefined;
    }
  | {
      titleRegexp: string | string[] | null | undefined;
    }
  | null;

type FetchMorePostsParamsType = {
  nextPage?: number;
  sort?: string;
  perPage?: number;
};

type PaginationPostQueryType = {
  paginationPostQuery: {
    items: PostType[];
    pageInfo: PageInfoType;
  };
};

type PaginationPostsFilterOptionsType = {
  filter?: PaginationVariablesType['filter'] | string | null;
  alias?: string | string[] | null;
  nextPage?: number;
  sort?: string;
  perPage?: number;
};

const getFilter = (filter?: string | null, alias?: string | string[] | null): GetFilterType => {
  if (!filter) {
    return null;
  }

  switch (filter) {
    case 'categoryAlias':
      return { categoryAlias: alias };

    case 'postAlias':
      return { titleRegexp: alias };

    case 'tagAlias':
      return { tagAlias: alias };

    default:
      return null;
  }
};

const resolveFilter = (filterOptions: PaginationPostsFilterOptionsType): PaginationVariablesType['filter'] | null => {
  if (typeof filterOptions.filter === 'string') {
    return getFilter(filterOptions.filter, filterOptions.alias) as PaginationVariablesType['filter'];
  }

  return filterOptions.filter ?? null;
};

export const usePaginationPostsQuery = (
  filterOptions: PaginationPostsFilterOptionsType,
  options: Record<string, unknown> = {},
  isLazyLoading = false,
  sort = '_ID_DESC',
) => {
  const queryResult = useQuery<PaginationPostQueryType, PaginationVariablesType>(
    itemsPagination({
      entity: 'post',
      sortType: 'SortEnum',
      fragment: postFragment,
    }),
    {
      variables: {
        filter: resolveFilter(filterOptions),
        nextPage: filterOptions.nextPage ?? 1,
        sort: filterOptions.sort ?? sort,
        perPage: filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
      },
      notifyOnNetworkStatusChange: options?.notifyOnNetworkStatusChange as boolean | undefined,
      skip: options?.skip as boolean | undefined,
    },
  );

  const { loading, error, data, fetchMore, refetch } = queryResult;

  const fetchMoreHandler = async ({ nextPage, sort: nextSort, perPage }: FetchMorePostsParamsType) => {
    await fetchMore(
      readQuery<PaginationPostQueryType, PaginationVariablesType>({
        query: 'paginationPostQuery',
        filter: resolveFilter(filterOptions),
        nextPage: nextPage ?? 1,
        sort: nextSort ?? filterOptions.sort ?? sort,
        perPage: perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,

        // ВАЖНО:
        // false = не конкатенировать старые items с новыми
        isLazyLoading: false,
      }),
    );
  };

  const refetchHandler = async (variables?: Partial<PaginationVariablesType>) => {
    await refetch(
      variables
        ? {
            filter: variables.filter ?? resolveFilter(filterOptions),
            nextPage: variables.nextPage ?? 1,
            sort: variables.sort ?? filterOptions.sort ?? sort,
            perPage: variables.perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
          }
        : undefined,
    );
  };

  return {
    loading,
    error,
    refetch: refetchHandler,
    data: data ?? {
      paginationPostQuery: {
        items: [],
        pageInfo: {
          hasPreviousPage: false,
          hasNextPage: false,
          currentPage: 1,
          perPage: 1,
          itemCount: 0,
          pageCount: 1,
        },
      },
    },
    fetchMore: fetchMoreHandler,
  };
};
